"use client";

import useSWR, { mutate } from "swr";
import { useAuth } from "@clerk/nextjs";

export interface CollectionMember {
  id: string;
  userId: string;
  role: string;
  joinedAt: Date;
  user: {
    displayName: string | null;
    username: string | null;
    avatar: string | null;
  };
}

export interface CollectionOwner {
  userId: string;
  displayName: string | null;
  username: string | null;
  avatar: string | null;
}

export interface OutgoingInvitation {
  id: string;
  role: string;
  createdAt: Date;
  invitedUser: {
    displayName: string | null;
    username: string | null;
    avatar: string | null;
  };
}

export interface MembersResponse {
  ok: boolean;
  data: {
    owner: CollectionOwner | null;
    members: CollectionMember[];
    pendingInvitations: OutgoingInvitation[];
  };
}

export interface CollectionInvitation {
  id: string;
  role: string;
  createdAt: Date;
  collection: {
    id: string;
    name: string;
    icon: string | null;
    color: string | null;
    itemCount: number;
  };
  invitedBy: {
    displayName: string | null;
    username: string | null;
    avatar: string | null;
  };
}

interface InvitationsResponse {
  ok: boolean;
  data: CollectionInvitation[];
}

export interface ActivityEntry {
  id: string;
  action: string;
  metadata: Record<string, unknown> | null;
  createdAt: Date;
  user: {
    displayName: string | null;
    username: string | null;
    avatar: string | null;
  };
}

interface ActivityResponse {
  ok: boolean;
  data: {
    items: ActivityEntry[];
    nextCursor: string | null;
  };
}

export function useCollectionMembers(collectionId: string | null) {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId && collectionId;

  const {
    data,
    error,
    isLoading,
    mutate: mutateMembers,
  } = useSWR<MembersResponse>(
    shouldFetch ? `/api/collections/${collectionId}/members` : null,
    { revalidateOnFocus: false },
  );

  return {
    owner: data?.data?.owner || null,
    members: data?.data?.members || [],
    pendingInvitations: data?.data?.pendingInvitations || [],
    isLoading: !shouldFetch || (isLoading && !data),
    error: error?.message || null,
    mutate: mutateMembers,
  };
}

export function usePendingInvitations() {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId;

  const {
    data,
    error,
    isLoading,
    mutate: mutateInvitations,
  } = useSWR<InvitationsResponse>(shouldFetch ? "/api/invitations" : null, {
    revalidateOnFocus: true,
    refreshInterval: 60000,
  });

  return {
    invitations: data?.data || [],
    count: data?.data?.length || 0,
    isLoading: !shouldFetch || (isLoading && !data),
    error: error?.message || null,
    mutate: mutateInvitations,
  };
}

export function useCollectionActivity(collectionId: string | null) {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId && collectionId;

  const { data, error, isLoading } = useSWR<ActivityResponse>(
    shouldFetch ? `/api/collections/${collectionId}/activity` : null,
    { revalidateOnFocus: false },
  );

  return {
    activities: data?.data?.items || [],
    nextCursor: data?.data?.nextCursor || null,
    isLoading: !shouldFetch || (isLoading && !data),
    error: error?.message || null,
  };
}

export async function inviteMember(
  collectionId: string,
  username: string,
  role: "viewer" | "editor",
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(`/api/collections/${collectionId}/members`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ username, role }),
    });

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(`/api/collections/${collectionId}/members`),
    );
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(`/api/collections/${collectionId}/activity`),
    );

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to invite member" };
  }
}

export async function acceptInvitation(
  invitationId: string,
): Promise<{ ok: boolean; collectionId?: string; error?: string }> {
  try {
    const response = await fetch(`/api/invitations/${invitationId}/accept`, {
      method: "POST",
    });

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate("/api/invitations");
    mutate(
      (key) => typeof key === "string" && key.startsWith("/api/collections"),
    );

    return { ok: true, collectionId: result.data?.collectionId };
  } catch {
    return { ok: false, error: "Failed to accept invitation" };
  }
}

export async function declineInvitation(
  invitationId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(`/api/invitations/${invitationId}/decline`, {
      method: "POST",
    });

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate("/api/invitations");

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to decline invitation" };
  }
}

export async function removeMember(
  collectionId: string,
  memberId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(
      `/api/collections/${collectionId}/members/${memberId}`,
      { method: "DELETE" },
    );

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate(`/api/collections/${collectionId}/members`);
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(`/api/collections/${collectionId}/activity`),
    );

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to remove member" };
  }
}

export async function cancelInvitation(
  collectionId: string,
  invitationId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(
      `/api/collections/${collectionId}/invitations/${invitationId}`,
      { method: "DELETE" },
    );

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate(`/api/collections/${collectionId}/members`);
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(`/api/collections/${collectionId}/activity`),
    );

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to cancel invitation" };
  }
}

export async function leaveCollection(
  collectionId: string,
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(
      `/api/collections/${collectionId}/members/leave`,
      { method: "POST" },
    );

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate(
      (key) => typeof key === "string" && key.startsWith("/api/collections"),
    );

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to leave collection" };
  }
}

export interface CollaboratorData {
  userId: string;
  username: string | null;
  displayName: string | null;
  avatar: string | null;
  sharedCollectionCount: number;
  relationship: "my_member" | "their_member" | "co_member";
  firstCollaboratedAt: string;
  sharedCollections: {
    name: string;
    icon: string | null;
    color: string | null;
  }[];
}

interface CollaboratorsResponse {
  ok: boolean;
  data: {
    collaborators: CollaboratorData[];
    total: number;
  };
}

export function useCollaborators() {
  const { isLoaded, userId } = useAuth();
  const shouldFetch = isLoaded && userId;

  const { data, error, isLoading } = useSWR<CollaboratorsResponse>(
    shouldFetch ? "/api/user/collaborators" : null,
    { revalidateOnFocus: false },
  );

  return {
    collaborators: data?.data?.collaborators || [],
    total: data?.data?.total || 0,
    isLoading: !shouldFetch || (isLoading && !data),
    error: error?.message || null,
  };
}

export async function updateMemberRole(
  collectionId: string,
  memberId: string,
  role: "viewer" | "editor",
): Promise<{ ok: boolean; error?: string }> {
  try {
    const response = await fetch(
      `/api/collections/${collectionId}/members/${memberId}`,
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ role }),
      },
    );

    const result = await response.json();

    if (!result.ok) {
      return { ok: false, error: result.error };
    }

    mutate(`/api/collections/${collectionId}/members`);
    mutate(
      (key) =>
        typeof key === "string" &&
        key.startsWith(`/api/collections/${collectionId}/activity`),
    );

    return { ok: true };
  } catch {
    return { ok: false, error: "Failed to update member role" };
  }
}
